/**
 * Modal based on Bulma
 */
import { translateComponent } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import Resizable from "/WebInterface/new-ui/modules/resizable/resizable.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

class Modal {
  constructor(args) {
    this.options = {
      title: "",
      uniqueId: utils.getUId(),
      body: "",
      style: "card",
      extraClasses: [],
      parent: document.body,
      animate: true,
      isFullScreen: false,
      showHeader: true,
      closable: true,
      disableScroll: true,
      hasCloseOption: true,
      hasResizeOption: false,
      destroyOnClose: true,
      backdrop: true,
      hasFooter: true,
      hasHeader: true,
      show: true,
      movable: false,
      appendFooter: null,
      prependFooter: null,
      animationInClass: "fadeInDown",
      animationOutClass: "fadeOutUp",
      zIndex: "",
      onBeforeShow: () => {},
      onAfterShow: () => {},
      onBeforeHide: () => {},
      onAfterHide: () => {},
      onBeforeResize: () => {},
      onAfterResize: () => {},
      onBeforeFullscreen: () => {},
      onAfterFullscreen: () => {},
      buttons: [
        {
          label: "ok",
          close: true,
          classes: ["mr-3"],
          onClick: () => {},
        },
      ],
      headerButtons: [],
      minHeight: "",
      minWidth: "",
      translateComponentMethod: translateComponent,
      ...args,
    };
    this.init();
    this.centerModal = this.centerModal.bind(this);
  }

  /**
   * Go through the provided buttons option and create the buttons.
   * @returns {void}
   */
  createButtons = () => {
    const { options, modal } = this;
    const that = this;
    modal.footer.innerHTML = "";

    if (options.prependFooter) {
      if (typeof options.prependFooter === "string") {
        modal.footer.innerHTML += utils.sanitizePure(options.prependFooter);
      } else {
        modal.footer.appendChild(options.prependFooter);
      }
    }

    const modalFooterButtonWrapper = _$("<div>");

    options.buttons.forEach((btn) => {
      const curBtn = utils.createElement(
        "button",
        btn.classes ? ["primary-button", ...btn.classes] : []
      );
      if (btn.id) {
        curBtn.id = `modal-btn-${btn.id}`;
      }
      if (btn.leftIconClasses) {
        const leftIconClasses = btn.leftIconClasses || [];
        const icon = utils.createElement("i", ["mr-2", ...leftIconClasses]);
        curBtn.append(icon);
      }
      if (btn.label) {
        const label = utils.createElement(
          "span",
          [],
          { "data-i18n-key": btn.label },
          null,
          btn.label
        );

        curBtn.append(label);
        // _$(curBtn).attr("data-i18n-key", btn.label)
      }
      // curBtn.innerHTML = btn.label;
      curBtn.addEventListener("click", (e) => {
        btn.onClick(e);
        if (btn.close) {
          that.hide(options.destroyOnClose);
        }
      });
      modalFooterButtonWrapper.append(curBtn);
    });

    modalFooterButtonWrapper
      .el()
      .classList.add("is-flex", "is-justify-content-end", "w-full");
    modal.footer.append(modalFooterButtonWrapper.el());

    if (options.appendFooter) {
      if (typeof options.appendFooter === "string") {
        modal.footer.innerHTML += utils.sanitizePure(options.appendFooter);
      } else {
        modal.footer.appendChild(options.appendFooter);
      }
    }
  };

  /**
   * Go through the provided buttons option and create the buttons.
   * @returns {void}
   */
  createHeaderButtons = () => {
    const { options, modal } = this;
    const that = this;

    options.headerButtons.forEach((btn) => {
      const curBtn = utils.createElement(
        "button",
        btn.classes ? ["primary-button", ...btn.classes] : []
      );

      curBtn.innerHTML = utils.sanitizePure(btn.label);

      if (btn.leftIconClasses) {
        const leftIconClasses = btn.leftIconClasses || [];
        const icon = utils.createElement("i", ["mr-2", ...leftIconClasses]);
        curBtn.prepend(icon);
      }

      curBtn.addEventListener("click", (e) => {
        btn.onClick(e);
        if (btn.close) {
          that.hide(options.destroyOnClose);
        }
      });
      modal.header.appendChild(curBtn);
    });
  };

  init = () => {
    const { options } = this;
    const that = this;
    const modal = {};
    if (options.animate) options.extraClasses.push("animate");
    const existingModal = _$("#modal_" + options.uniqueId).el();
    this.root =
      existingModal ||
      utils.createElement(
        "div",
        ["modal", "is-hidden"].concat(options.extraClasses),
        { "aria-hidden": options.show }
      );

    const modalType = options.style === "card" ? "card" : "content";
    this.root.setAttribute("id", "modal_" + options.uniqueId);

    if (options.zIndex) {
      this.root.style.zIndex = options.zIndex;
    }

    const animation = options.animate
      ? ["animate", options.animationInClass]
      : [];
    that.modal = modal;

    if (!existingModal) {
      this.content =
        options.style === "card"
          ? utils.createElement("div", ["modal-card"].concat(animation))
          : utils.createElement("div", ["modal-content"].concat(animation));

      if (options.movable) {
        this.root.classList.add("resizable-container");
        if (options.minHeight) {
          this.root.style.minHeight = options.minHeight;
          this.root.style.height = options.minHeight;
        }
        if (options.minWidth) {
          this.root.style.minWidth = options.minWidth;
          this.root.style.width = options.minWidth;
        }
      } else {
        this.content.classList.add("modal-card-responsive");
        if (options.minHeight) {
          this.content.style.minHeight = options.minHeight;
        }
        if (options.minWidth) {
          this.content.style.minWidth = options.minWidth;
        }
      }

      const createCardStructure = () => {
        if (options.showHeader) {
          modal.header = utils.createElement("header", [
            "modal-" + modalType + "-head",
            "header",
          ]);

          modal.headerTitle = utils.createElement("p", [
            "modal-" + modalType + "-title",
            "text-primaryMain",
            "is-capitalized",
          ]);

          if (options.title) {
            modal.headerTitle.setAttribute("data-i18n-key", options.title);
          }
          modal.headerTitle.innerHTML = utils.sanitizePure(options.title);

          modal.header.appendChild(modal.headerTitle);

          modal.header.addEventListener("mousedown", () => {
            this.moveModalOnTop();
          });

          if (options.headerButtons) {
            this.createHeaderButtons();
          }

          if (options.hasResizeOption) {
            modal.headerResizeButton = utils.createElement("button", [
              "resize-button",
              "header-btn",
              "is-cursor-pointer",
            ]);

            let headerResizeIcon = utils.createElement("i", [
              "icon-resize-full",
              "text-primaryMain",
            ]);

            modal.headerResizeButton.appendChild(headerResizeIcon);
            modal.header.appendChild(modal.headerResizeButton);
          }

          if (options.hasCloseOption) {
            modal.headerCloseButton = utils.createElement("button", [
              "remove-button",
              "header-btn",
              "is-cursor-pointer",
            ]);

            let headerCloseIcon = utils.createElement("i", [
              "icon-remove",
              "text-primaryMain",
            ]);

            modal.headerCloseButton.addEventListener("click", (event) => {
              event.stopPropagation();
              that.hide(options.destroyOnClose);
            });

            modal.headerCloseButton.appendChild(headerCloseIcon);
            modal.header.appendChild(modal.headerCloseButton);
          }
        }

        modal.body = utils.createElement("section", [
          "modal-" + modalType + "-body",
        ]);

        if (typeof options.body === "string") {
          modal.body.innerHTML += options.body;
        } else {
          modal.body.appendChild(options.body);
        }

        /** @param {HTMLElement} */
        modal.footer = utils.createElement("footer", [
          "modal-" + modalType + "-foot",
          "is-flex-direction-column",
          "is-justify-content-end",
        ]);
      };

      createCardStructure();
      this.createButtons();

      if (modal.header) this.content.appendChild(modal.header);
      if (modal.body) this.content.appendChild(modal.body);
      if (modal.footer && options.hasFooter) {
        _$(this.root).addClass("has-footer");
        this.content.appendChild(modal.footer);
      }
      if (options.backdrop) {
        const background = utils.createElement("div", ["modal-background"]);
        this.root.appendChild(background);

        if (options.closable) {
          background.addEventListener("click", () => {
            that.hide(options.destroyOnClose);
          });
        }
      }

      this.root.appendChild(this.content);

      if (options.movable) {
        this.addResizable();
      }

      options.parent.appendChild(that.root);
    }

    that.keyupListener = (event) => {
      if (!that.root.classList.contains("is-active")) {
        return;
      }
      let key = event.key || event.keyCode;
      if (options.closable) {
        if (key === "Escape" || key === "Esc" || key === 27) {
          that.hide(options.destroyOnClose);
        }
      }
    };

    if (options.closable) {
      document.addEventListener("keyup", that.keyupListener);
    }

    if (options.show) {
      that.show();
      if (options.disableScroll) {
        _$("html").addClass("is-clipped");
      }
    }

    const firstInput = this.root.querySelector("input, textarea");
    try {
      if (firstInput && firstInput.setSelectionRange)
        firstInput.setSelectionRange(-1, -1);
      this.root.querySelector("input, textarea, button").focus();
    } catch (error) {}

    options.translateComponentMethod(that.root);
  };

  update = (newOptions = {}) => {
    this.options = { ...this.options, ...newOptions };
    const { options, modal } = this;

    let tempRoot = this.content;
    if (options.movable) tempRoot = this.root;

    if (newOptions.minHeight) {
      if (options.movable) {
        tempRoot.style.height = options.minHeight;
      }
      tempRoot.style.minHeight = options.minHeight;
    }

    if (newOptions.minWidth) {
      if (options.movable) {
        tempRoot.style.width = options.minWidth;
      }
      tempRoot.style.minWidth = options.minWidth;
    }

    if (newOptions.body) {
      if (typeof options.body === "string") {
        modal.body.innerHTML = options.body;
      } else {
        modal.body.innerHTML = "";
        modal.body.appendChild(options.body);
      }
    }

    if (options.title) {
      modal.headerTitle.setAttribute("data-i18n-key", options.title);
    }
    modal.headerTitle.innerHTML = utils.sanitizePure(options.title);

    if (newOptions.buttons) {
      this.createButtons();
    }

    if (!options.hasCloseOption) {
      if (modal.headerCloseButton)
        modal.headerCloseButton.classList.add("is-hidden");
    } else {
      if (modal.headerCloseButton)
        modal.headerCloseButton.classList.remove("is-hidden");
    }

    const container = this.root;
    utils.centerModal({ child: container });
    options.translateComponentMethod(this.root);
  };

  centerModal = () => {
    const screen = document.documentElement;
    if (screen.clientWidth > 767) {
      utils.centerModal({ child: this.root });
    }
  };

  moveModalOnTop = () => {
    _$(".modal").removeClass("modal-on-top");
    _$(this.root).addClass("modal-on-top");
  };

  show = (doNotReset) => {
    if (this.root) {
      const { options } = this;

      if (!doNotReset) {
        _$("#modal_" + options.uniqueId).remove();
        options.parent.appendChild(this.root);
      }

      this.moveModalOnTop();

      options.onBeforeShow(this.root);
      if (options.animate) {
        const modalContent = this.root.querySelector(".animate");
        modalContent.classList.add(options.animationInClass);
      }
      this.root.classList.add("is-active");
      this.root.classList.remove("is-hidden");
      this.root.setAttribute("aria-hidden", "true");
      _$("html").addClass("crushftp-opened-modal");

      if (options.disableScroll) {
        setTimeout(function () {
          _$("html").addClass("is-clipped");
        }, 100);
      }

      this.centerModal();

      window.addEventListener("resize", this.centerModal);
      options.onAfterShow(this.root);
    }
  };

  hide = (destroy) => {
    const { options } = this;
    const that = this;
    options.onBeforeHide();
    const continueClose = () => {
      that.root.classList.remove("is-active");
      that.root.setAttribute("aria-hidden", "false");

      const allModals = _$(".modal").sel();
      const modalIndex = allModals.findIndex((modal) => {
        if (modal.getAttribute("aria-hidden") === "true") {
          return true;
        }
        return false;
      });
      if (modalIndex === -1) {
        _$("html").removeClass("crushftp-opened-modal");
      }
      if (options.disableScroll) {
        _$("html").removeClass("is-clipped");
      }

      if (options.movable) {
        that.root.classList.add("is-hidden");
      }
      if (options.closable) {
        document.removeEventListener("keyup", that.keyupListener);
      }
      if (options.animate) {
        const modalContent = that.root.querySelector(".animate");
        if (modalContent) {
          modalContent.classList.remove(options.animationInClass);
          modalContent.classList.remove(options.animationOutClass);
        }
      }
      if (destroy) {
        that.root.remove();
        for (var key in that) {
          delete that[key];
        }
      }
      options.onAfterHide();
    };
    if (options.animate) {
      const modalContent = that.root.querySelector(".animate");
      if (modalContent) {
        modalContent.classList.remove(options.animationInClass);
        modalContent.classList.add(options.animationOutClass);
      }
      setTimeout(() => {
        continueClose();
      }, 50);
    } else {
      continueClose();
    }

    removeEventListener("resize", this.centerModal);
  };

  visible = () => {
    return (
      this.root &&
      this.root.classList &&
      this.root.classList.contains("is-active")
    );
  };

  addResizable = async () => {
    const { options } = this;

    this.root.querySelector("div").style.margin = "unset";
    const resizable = new Resizable({
      id: `resizable-uniqueId-${options.uniqueId}`,
      title: `resizable-title-${options.title}`,
      container: this.root,
      canFullScreen: true,
      isFullScreen: options.isFullScreen,
      onBeforeResize: options.onBeforeResize,
      onAfterResize: options.onAfterResize,
      onBeforeFullscreen: options.onBeforeFullscreen,
      onAfterFullscreen: options.onAfterFullscreen,
    });
    resizable.init();
  };
}
export default Modal;
